import styled from '@emotion/styled';

import { Player } from '@lottiefiles/react-lottie-player';

import Checkbox from '@/components/Checkbox/Checkbox';
import Error from '@/images/ErrorX.svg';
import LoadingAnimation from '@/images/LoadingAnimation.json';
import { Typography } from '@mui/material';
import { ReactNode } from 'react';

const ImageContainer = styled.div<{ isSelected?: boolean }>`
    width: 100%;
    height: 80%;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: 1rem;
    border-radius: 12px;
    background-color: ${({ theme }) => theme.colors.settings};
    border: ${({ isSelected, theme }) =>
        `${isSelected ? '2px' : '4px'} solid ${isSelected ? theme.colors.secondary.main : theme.colors.black[100]}`};

    &:hover .video-container-hover {
        display: block;
    }
`;

const CheckboxContainer = styled.div`
    position: absolute;
    top: 1rem;
    left: 1rem;
`;

const Image = styled.div<{ src: string; backgroundColor?: string; size: string; blur?: boolean }>`
    background: url(${({ src }) => src}) no-repeat center;
    background-size: ${({ size }) => size};
    height: 100%;
    width: 100%;
    background-color: ${({ backgroundColor }) => backgroundColor};
    border-radius: 12px;
    filter: ${({ blur }) => (blur ? 'blur(6px)' : 'none')};
`;

const LoadingContainer = styled.div`
    width: 100%;
    height: 100%;
    position: relative;
`;

const Loading = styled(Player)<{ isSmall?: boolean }>`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    height: ${({ isSmall }) => (isSmall ? 8 : 16)}rem;
`;

interface Props {
    errorText: string;
    src: string;
    isError?: boolean;
    isLoading?: boolean;
    size?: 'contain' | 'cover';
    backgroundColor?: string;
    children?: ReactNode;
    className?: string;
    isSelected?: boolean;
    onSelect?: () => void;
    withCheckbox?: boolean;
    disableSelect?: boolean;
}

function ImageContent({ size, backgroundColor, src, errorText, isError, isLoading }: Props) {
    if (isError) {
        return (
            <>
                <img src={Error} />
                <Typography variant="h4" fontWeight={600}>
                    {errorText}
                </Typography>
            </>
        );
    } else if (isLoading) {
        return (
            <LoadingContainer>
                <Image src={src} backgroundColor={backgroundColor} size={size || 'cover'} blur />
                <Loading src={LoadingAnimation} autoplay loop />
            </LoadingContainer>
        );
    }

    return <Image src={src} backgroundColor={backgroundColor} size={size || 'cover'} />;
}

function LibraryItemImage({ children, className, isSelected, onSelect, withCheckbox, disableSelect, ...props }: Props) {
    return (
        <ImageContainer className={className} isSelected={isSelected}>
            <ImageContent {...props} />
            {children}
            {withCheckbox && (
                <CheckboxContainer>
                    <Checkbox
                        checked={!!isSelected}
                        onClick={event => {
                            event.stopPropagation();
                            onSelect?.();
                        }}
                        disabled={disableSelect}
                    />
                </CheckboxContainer>
            )}
        </ImageContainer>
    );
}

export default LibraryItemImage;
